import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '@/components/ui/Button';
import ParchmentCard from '@/components/ui/ParchmentCard';
import useAuth from '@/hooks/useAuth';

const avatars = [
  { label: 'Aventureira', src: '/avatars/avatar_1.png' },
  { label: 'Pipo', src: '/characters/pipo-avatar.png' },
  { label: 'Papagaio', src: '/characters/papagaio.png' },
];

export default function RegisterPage() {
  const { handleRegister } = useAuth();
  const [form, setForm] = useState({ email: '', password: '', username: '', avatarUrl: avatars[0].src });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const avatarRefs = useRef({});

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      await handleRegister(form);
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível criar a conta.');
    } finally {
      setLoading(false);
    }
  };

  const selectAvatar = (avatar) => {
    setForm({ ...form, avatarUrl: avatar.src });
    avatarRefs.current[avatar.src]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  };

  return (
    <main className="grid h-full place-items-center overflow-y-auto bg-background px-3 py-4 sm:px-4 sm:py-6">
      <ParchmentCard className="mx-auto min-h-fit w-full max-w-md px-4 pb-5 pt-4 sm:px-5">
        <div className="mb-2 flex items-center justify-between gap-3">
          <Link className="text-sm font-bold text-accent hover:text-accent" to="/">
            ← Início
          </Link>
          <picture>
            <source srcSet="/branding/logo-hakkencity.webp" type="image/webp" />
            <img src="/branding/logo-hakkencity-opt.png" alt="Hakken City" className="h-20 w-auto object-contain sm:h-28" />
          </picture>
          <span className="w-12" />
        </div>

        <h1 className="mt-2 font-urban text-xl font-black text-fg sm:text-2xl">Nova aventura</h1>
        <form className="mt-4 grid min-w-0 gap-3 sm:mt-5" onSubmit={submit}>
          <div className="min-w-0">
            <p className="mb-2 text-sm font-bold text-fg/70">Escolhe o teu avatar</p>
            <div className="w-full min-w-0 max-w-full overflow-x-auto overflow-y-visible pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="flex w-max snap-x snap-mandatory gap-3 px-1 sm:gap-4">
              {avatars.map((avatar) => {
                const selected = form.avatarUrl === avatar.src;

                return (
                  <div key={avatar.src} ref={(node) => { avatarRefs.current[avatar.src] = node; }} className="shrink-0 snap-center">
                    <button
                      type="button"
                      onClick={() => selectAvatar(avatar)}
                      className={`h-36 w-28 overflow-hidden rounded-xl border-[3px] bg-surfaceAlt p-1 transition duration-200 sm:h-44 sm:w-32 sm:border-4 ${
                        selected
                          ? 'border-accent'
                          : 'border-edge opacity-80'
                      }`}
                      aria-label={avatar.label}
                    >
                      <img src={avatar.src} alt={avatar.label} className="h-full w-full rounded-lg object-cover object-top" />
                    </button>
                    <span className={`mt-2 block text-center text-xs font-bold ${selected ? 'text-fg' : 'text-fg/50'}`}>
                      {avatar.label}
                    </span>
                  </div>
                );
              })}
              </div>
            </div>
          </div>
          <input className="min-w-0 rounded-xl border-2 border-edge bg-surfaceAlt px-4 py-2.5 text-fg outline-none focus:border-accent sm:py-3" placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input className="min-w-0 rounded-xl border-2 border-edge bg-surfaceAlt px-4 py-2.5 text-fg outline-none focus:border-accent sm:py-3" placeholder="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
          <input className="min-w-0 rounded-xl border-2 border-edge bg-surfaceAlt px-4 py-2.5 text-fg outline-none focus:border-accent sm:py-3" placeholder="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          {error && <p className="text-sm font-bold text-pirate-red">{error}</p>}
          <Button type="submit" fullWidth variant="gold" disabled={loading}>{loading ? 'A criar...' : 'Criar conta'}</Button>
        </form>
        <Link className="mt-4 block text-center text-sm font-bold text-accent" to="/login">Já tenho conta</Link>
      </ParchmentCard>
    </main>
  );
}
